import React, { useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import type { Id, ServiceTemplatesResponse } from "../types";

// Configuración del tratamiento — Frontend Operativo de Fase 2 + Fase 2B
// (EN DESARROLLO, 2026-08-23). Único lugar donde admin/director definen,
// por servicio, la plantilla habitual de consumo de insumos y de uso de
// equipamiento — precargada después por la pantalla de Ejecución (pestaña
// "Tratamiento" en /pacientes/:id/historia).
//
// Alcance explícitamente acotado por el usuario: SÓLO CRUD de
// service_consumption_templates/service_equipment_templates. No hay acá
// (ni se agrega) administración de productos, proveedores, depósitos ni
// activos — esos catálogos se asumen ya cargados (seed/backend existente).
//
// Versionado: crear una nueva plantilla NO edita la anterior — la
// reemplaza con una versión nueva activa (mismo criterio que
// consent_templates). No hay botón "editar item"; se rearma la lista
// completa y se guarda como versión nueva.

interface DraftConsumptionItem {
  key: string;
  product_id: string;
  quantity: string;
  required: boolean;
}
interface DraftEquipmentItem {
  key: string;
  asset_id: string;
  capacity_unit: string;
  expected_quantity: string;
  required: boolean;
}

const CAPACITY_UNITS = ["SHOT", "PULSE", "HOUR", "MINUTE", "CYCLE", "SESSION", "USE"];

function newKey() {
  return `k-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function TreatmentTemplates() {
  const { user } = useAuth();
  const { show } = useToast();
  const canEdit = !!user && ["admin", "director"].includes(user.role_code);

  const [services, setServices] = useState<{ id: Id; code: string; name: string }[]>([]);
  const [products, setProducts] = useState<{ id: Id; sku: string; name: string }[]>([]);
  const [assets, setAssets] = useState<{ id: Id; asset_tag: string; name: string }[]>([]);
  const [serviceId, setServiceId] = useState("");
  const [current, setCurrent] = useState<ServiceTemplatesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingConsumption, setSavingConsumption] = useState(false);
  const [savingEquipment, setSavingEquipment] = useState(false);

  const [consumptionItems, setConsumptionItems] = useState<DraftConsumptionItem[]>([]);
  const [equipmentItems, setEquipmentItems] = useState<DraftEquipmentItem[]>([]);

  useEffect(() => {
    if (!canEdit) return;
    Promise.all([api.getServicesLookup(), api.getInventoryProductsLookup(), api.getEquipmentAssetsLookup()])
      .then(([svc, prod, ast]) => {
        setServices(svc);
        setProducts(prod);
        setAssets(ast);
      })
      .catch((e) => show(e instanceof ApiClientError ? e.message : "No se pudieron cargar los catálogos.", "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  async function loadService(id: string) {
    setServiceId(id);
    setCurrent(null);
    setConsumptionItems([]);
    setEquipmentItems([]);
    if (!id) return;
    setLoading(true);
    try {
      const data = await api.getServiceTemplates(Number(id));
      setCurrent(data);
      setConsumptionItems(
        data.consumption_template
          ? data.consumption_template.items.map((it) => ({
              key: newKey(),
              product_id: String(it.product_id),
              quantity: String(it.quantity),
              required: it.required,
            }))
          : []
      );
      setEquipmentItems(
        data.equipment_template
          ? data.equipment_template.items.map((it) => ({
              key: newKey(),
              asset_id: String(it.asset_id),
              capacity_unit: it.capacity_unit,
              expected_quantity: String(it.expected_quantity),
              required: it.required,
            }))
          : []
      );
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo cargar la plantilla del servicio.", "error");
    } finally {
      setLoading(false);
    }
  }

  async function saveConsumption() {
    if (!serviceId) return;
    const items = consumptionItems
      .filter((it) => it.product_id && Number(it.quantity) > 0)
      .map((it) => ({ product_id: Number(it.product_id), quantity: Number(it.quantity), required: it.required }));
    setSavingConsumption(true);
    try {
      await api.saveConsumptionTemplate(Number(serviceId), items);
      show(`Plantilla de consumo guardada (versión nueva, ${items.length} línea(s)).`, "success");
      loadService(serviceId);
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo guardar la plantilla de consumo.", "error");
    } finally {
      setSavingConsumption(false);
    }
  }

  async function saveEquipment() {
    if (!serviceId) return;
    const items = equipmentItems
      .filter((it) => it.asset_id && it.capacity_unit && Number(it.expected_quantity) > 0)
      .map((it) => ({
        asset_id: Number(it.asset_id),
        capacity_unit: it.capacity_unit,
        expected_quantity: Number(it.expected_quantity),
        required: it.required,
      }));
    setSavingEquipment(true);
    try {
      await api.saveEquipmentTemplate(Number(serviceId), items);
      show(`Plantilla de equipamiento guardada (versión nueva, ${items.length} línea(s)).`, "success");
      loadService(serviceId);
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo guardar la plantilla de equipamiento.", "error");
    } finally {
      setSavingEquipment(false);
    }
  }

  if (!canEdit) {
    return (
      <div className="placeholder-page">
        <h1>Plantillas de tratamiento</h1>
        <p className="meta">Sólo admin/director pueden configurar plantillas de tratamiento.</p>
      </div>
    );
  }

  return (
    <div className="placeholder-page">
      <h1>Plantillas de tratamiento</h1>
      <p className="meta">
        Configuración del consumo esperado de insumos y del uso esperado de equipamiento por servicio. Esto precarga la
        pantalla de Ejecución del tratamiento (pestaña "Tratamiento" en la historia clínica) — el consumo real registrado
        ahí puede diferir, deliberadamente: acá sólo se define lo esperado.
      </p>

      <div className="row" style={{ margin: "12px 0" }}>
        <select value={serviceId} onChange={(e: any) => loadService(e.target.value)}>
          <option value="">Elegí un servicio...</option>
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {loading && <div className="loading-strip">Cargando plantilla...</div>}

      {serviceId && !loading && (
        <>
          <div className="chart-encounter-card">
            <div className="head">
              <h4 style={{ fontSize: 13, margin: 0 }}>Plantilla de consumo (insumos)</h4>
              <span className="meta">
                {current?.consumption_template ? `versión activa: v${current.consumption_template.version}` : "sin plantilla configurada"}
              </span>
            </div>
            {consumptionItems.map((it) => (
              <div className="chart-item-row" key={it.key}>
                <select
                  value={it.product_id}
                  onChange={(e: any) =>
                    setConsumptionItems((prev) => prev.map((r) => (r.key === it.key ? { ...r, product_id: e.target.value } : r)))
                  }
                >
                  <option value="">Insumo...</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Cantidad esperada"
                  value={it.quantity}
                  onChange={(e: any) =>
                    setConsumptionItems((prev) => prev.map((r) => (r.key === it.key ? { ...r, quantity: e.target.value } : r)))
                  }
                />
                <label className="meta" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <input
                    type="checkbox"
                    checked={it.required}
                    onChange={(e: any) =>
                      setConsumptionItems((prev) => prev.map((r) => (r.key === it.key ? { ...r, required: e.target.checked } : r)))
                    }
                  />
                  obligatorio
                </label>
                <button className="btn-ghost" onClick={() => setConsumptionItems((prev) => prev.filter((r) => r.key !== it.key))}>
                  ✕
                </button>
              </div>
            ))}
            <div className="row">
              <button
                className="btn-ghost"
                onClick={() =>
                  setConsumptionItems((prev) => [...prev, { key: newKey(), product_id: "", quantity: "", required: true }])
                }
              >
                + Agregar insumo esperado
              </button>
              <button className="btn-primary" disabled={savingConsumption} onClick={saveConsumption}>
                Guardar plantilla de consumo (nueva versión)
              </button>
            </div>
          </div>

          <div className="chart-encounter-card">
            <div className="head">
              <h4 style={{ fontSize: 13, margin: 0 }}>Plantilla de equipamiento</h4>
              <span className="meta">
                {current?.equipment_template ? `versión activa: v${current.equipment_template.version}` : "sin plantilla configurada"}
              </span>
            </div>
            {equipmentItems.map((it) => (
              <div className="chart-item-row" key={it.key}>
                <select
                  value={it.asset_id}
                  onChange={(e: any) =>
                    setEquipmentItems((prev) => prev.map((r) => (r.key === it.key ? { ...r, asset_id: e.target.value } : r)))
                  }
                >
                  <option value="">Equipo...</option>
                  {assets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
                <select
                  value={it.capacity_unit}
                  onChange={(e: any) =>
                    setEquipmentItems((prev) => prev.map((r) => (r.key === it.key ? { ...r, capacity_unit: e.target.value } : r)))
                  }
                >
                  <option value="">Unidad...</option>
                  {CAPACITY_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Cantidad esperada (ej. 600 disparos)"
                  value={it.expected_quantity}
                  onChange={(e: any) =>
                    setEquipmentItems((prev) => prev.map((r) => (r.key === it.key ? { ...r, expected_quantity: e.target.value } : r)))
                  }
                />
                <label className="meta" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <input
                    type="checkbox"
                    checked={it.required}
                    onChange={(e: any) =>
                      setEquipmentItems((prev) => prev.map((r) => (r.key === it.key ? { ...r, required: e.target.checked } : r)))
                    }
                  />
                  obligatorio
                </label>
                <button className="btn-ghost" onClick={() => setEquipmentItems((prev) => prev.filter((r) => r.key !== it.key))}>
                  ✕
                </button>
              </div>
            ))}
            <div className="row">
              <button
                className="btn-ghost"
                onClick={() =>
                  setEquipmentItems((prev) => [
                    ...prev,
                    { key: newKey(), asset_id: "", capacity_unit: "", expected_quantity: "", required: true },
                  ])
                }
              >
                + Agregar equipamiento esperado
              </button>
              <button className="btn-primary" disabled={savingEquipment} onClick={saveEquipment}>
                Guardar plantilla de equipamiento (nueva versión)
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
